'use client';

// PDF Generator utility for YASH Computers
// Uses jsPDF for all document generation

export const INSTITUTE_BRANDING = {
  name: 'YASH Computers',
  fullName: 'YASH Computers — Computer Education Institute',
  address: 'Near Bus Stand, Main Road, Your City — Karnataka',
  keonics: 'KEONICS Franchisee — A Govt of Karnataka Enterprise',
  phone: '+91-XXXXXXXXXX',
  email: 'admin@yashcomputers.com',
  website: 'www.yashcomputers.com',
  logoPath: '/assets/images/logo1-1786300024701.jpg',
};

export interface ApplicationFormData {
  // Personal Info
  fullName: string;
  dob: string;
  gender: string;
  phone: string;
  email: string;
  address: string;
  guardianName: string;
  guardianPhone: string;
  // Academic
  qualification: string;
  course: string;
  batch: string;
  hourPeriod: string;
  // Photo
  photoDataUrl?: string;
  // Meta
  applicationDate: string;
  applicationNo: string;
}

export interface PaymentReceiptData {
  receiptNo: string;
  studentName: string;
  studentPhone: string;
  studentEmail: string;
  course: string;
  batch: string;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  transactionId: string;
  razorpayOrderId?: string;
  razorpayPaymentId?: string;
  installmentLabel?: string;
  totalFee?: number;
  paidSoFar?: number;
  balance?: number;
}

/**
 * Generate Application Form PDF
 */
export async function generateApplicationPDF(data: ApplicationFormData): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const pageW = 210;
  const margin = 15;
  let y = margin;

  // ── Header / Branding ──
  // Blue header bar
  doc.setFillColor(30, 58, 95);
  doc.rect(0, 0, pageW, 38, 'F');

  // Logo placeholder (white circle)
  doc.setFillColor(255, 255, 255);
  doc.circle(margin + 10, 19, 10, 'F');
  doc.setFontSize(7);
  doc.setTextColor(30, 58, 95);
  doc.text('YASH', margin + 6.5, 18);
  doc.text('Comp.', margin + 5.5, 22);

  // Try to embed logo image
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = INSTITUTE_BRANDING.logoPath;
    await new Promise<void>((resolve) => {
      img.onload = () => {
        try {
          doc.addImage(img, 'JPEG', margin + 1, 9, 18, 18);
        } catch (_) { /* fallback to text */ }
        resolve();
      };
      img.onerror = () => resolve();
      setTimeout(resolve, 1500);
    });
  } catch (_) { /* ignore */ }

  // Institute name
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text(INSTITUTE_BRANDING.name, margin + 25, 14);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(INSTITUTE_BRANDING.address, margin + 25, 20);
  doc.text(INSTITUTE_BRANDING.keonics, margin + 25, 26);
  doc.text(`${INSTITUTE_BRANDING.phone}  |  ${INSTITUTE_BRANDING.email}`, margin + 25, 32);

  y = 48;

  // ── Application Title ──
  doc.setFillColor(240, 247, 255);
  doc.rect(margin, y - 4, pageW - margin * 2, 14, 'F');
  doc.setFontSize(13);
  doc.setTextColor(30, 58, 95);
  doc.setFont('helvetica', 'bold');
  doc.text('STUDENT APPLICATION FORM', pageW / 2, y + 4, { align: 'center' });

  y += 16;

  // Application No & Date
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`Application No: ${data.applicationNo}`, margin, y);
  doc.text(`Date: ${data.applicationDate}`, pageW - margin, y, { align: 'right' });

  y += 10;

  // ── Photo Box ──
  const photoX = pageW - margin - 35;
  const photoY = y;
  doc.setDrawColor(30, 58, 95);
  doc.setLineWidth(0.5);
  doc.rect(photoX, photoY, 35, 42);
  if (data.photoDataUrl) {
    try {
      doc.addImage(data.photoDataUrl, 'JPEG', photoX + 1, photoY + 1, 33, 40);
    } catch (_) {
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('Photo', photoX + 12, photoY + 22);
    }
  } else {
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('Affix', photoX + 12, photoY + 18);
    doc.text('Photo', photoX + 11, photoY + 24);
  }

  // ── Section: Personal Information ──
  const sectionW = photoX - margin - 5;
  drawSection(doc, 'PERSONAL INFORMATION', margin, y, sectionW);
  y += 8;

  const personalFields = [
    ['Full Name', data.fullName],
    ['Date of Birth', data.dob],
    ['Gender', data.gender],
    ['Phone', data.phone],
    ['Email', data.email || '—'],
    ['Address', data.address],
    ['Guardian Name', data.guardianName],
    ['Guardian Phone', data.guardianPhone],
  ];

  for (const [label, value] of personalFields) {
    drawField(doc, label, value, margin, y, sectionW);
    y += 7;
  }

  y = Math.max(y, photoY + 48);
  y += 5;

  // ── Section: Course Information ──
  drawSection(doc, 'COURSE INFORMATION', margin, y, pageW - margin * 2);
  y += 8;

  const courseFields = [
    ['Course', data.course],
    ['Batch', data.batch || '—'],
    ['Time Slot', data.hourPeriod],
    ['Qualification', data.qualification],
  ];

  const halfW = (pageW - margin * 2 - 5) / 2;
  let col = 0;
  for (const [label, value] of courseFields) {
    const xPos = margin + col * (halfW + 5);
    drawField(doc, label, value, xPos, y, halfW);
    col++;
    if (col === 2) { col = 0; y += 7; }
  }
  if (col !== 0) y += 7;
  y += 5;

  // ── Declaration ──
  doc.setFillColor(248, 250, 252);
  doc.rect(margin, y, pageW - margin * 2, 22, 'F');
  doc.setDrawColor(200, 210, 220);
  doc.rect(margin, y, pageW - margin * 2, 22);
  doc.setFontSize(8);
  doc.setTextColor(60, 60, 60);
  doc.setFont('helvetica', 'italic');
  const declaration = 'I hereby declare that the information provided above is true and correct to the best of my knowledge. I agree to abide by the rules and regulations of YASH Computers.';
  const lines = doc.splitTextToSize(declaration, pageW - margin * 2 - 10);
  doc.text(lines, margin + 5, y + 7);
  y += 28;

  // Signature lines
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(30, 58, 95);
  doc.setLineWidth(0.3);
  doc.setDrawColor(30, 58, 95);
  doc.line(margin, y + 8, margin + 50, y + 8);
  doc.line(pageW - margin - 50, y + 8, pageW - margin, y + 8);
  doc.setFontSize(8);
  doc.text('Student Signature', margin, y + 13);
  doc.text('Authorized Signature', pageW - margin - 50, y + 13);
  y += 20;

  // ── Footer ──
  addFooter(doc, pageW, margin);

  doc.save(`Application_${data.applicationNo}_${data.fullName.replace(/\s+/g, '_')}.pdf`);
}

/**
 * Generate Payment Receipt PDF
 */
export async function generateReceiptPDF(data: PaymentReceiptData): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const pageW = 210;
  const margin = 15;
  let y = margin;

  // ── Header ──
  doc.setFillColor(30, 58, 95);
  doc.rect(0, 0, pageW, 38, 'F');

  // Logo
  doc.setFillColor(255, 255, 255);
  doc.circle(margin + 10, 19, 10, 'F');
  doc.setFontSize(7);
  doc.setTextColor(30, 58, 95);
  doc.text('YASH', margin + 6.5, 18);
  doc.text('Comp.', margin + 5.5, 22);

  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = INSTITUTE_BRANDING.logoPath;
    await new Promise<void>((resolve) => {
      img.onload = () => {
        try { doc.addImage(img, 'JPEG', margin + 1, 9, 18, 18); } catch (_) { }
        resolve();
      };
      img.onerror = () => resolve();
      setTimeout(resolve, 1500);
    });
  } catch (_) { }

  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text(INSTITUTE_BRANDING.name, margin + 25, 14);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(INSTITUTE_BRANDING.address, margin + 25, 20);
  doc.text(INSTITUTE_BRANDING.keonics, margin + 25, 26);
  doc.text(`${INSTITUTE_BRANDING.phone}  |  ${INSTITUTE_BRANDING.email}`, margin + 25, 32);

  y = 48;

  // ── Receipt Title ──
  doc.setFillColor(240, 247, 255);
  doc.rect(margin, y - 4, pageW - margin * 2, 14, 'F');
  doc.setFontSize(13);
  doc.setTextColor(30, 58, 95);
  doc.setFont('helvetica', 'bold');
  doc.text('FEE PAYMENT RECEIPT', pageW / 2, y + 4, { align: 'center' });

  y += 16;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`Receipt No: ${data.receiptNo}`, margin, y);
  doc.text(`Date: ${data.paymentDate}`, pageW - margin, y, { align: 'right' });

  y += 12;

  // ── Student Details ──
  drawSection(doc, 'STUDENT DETAILS', margin, y, pageW - margin * 2);
  y += 8;

  const studentFields = [
    ['Student Name', data.studentName],
    ['Phone', data.studentPhone],
    ['Email', data.studentEmail || '—'],
    ['Course', data.course],
    ['Batch', data.batch || '—'],
  ];

  const halfW = (pageW - margin * 2 - 5) / 2;
  let col = 0;
  for (const [label, value] of studentFields) {
    const xPos = margin + col * (halfW + 5);
    drawField(doc, label, value, xPos, y, halfW);
    col++;
    if (col === 2) { col = 0; y += 7; }
  }
  if (col !== 0) y += 7;
  y += 8;

  // ── Payment Details ──
  drawSection(doc, 'PAYMENT DETAILS', margin, y, pageW - margin * 2);
  y += 8;

  const paymentFields = [
    ['Payment Method', data.paymentMethod],
    ['Transaction ID', data.transactionId],
    ...(data.razorpayOrderId ? [['Razorpay Order ID', data.razorpayOrderId]] : []),
    ...(data.razorpayPaymentId ? [['Razorpay Payment ID', data.razorpayPaymentId]] : []),
    ...(data.installmentLabel ? [['Installment', data.installmentLabel]] : []),
  ];

  for (const [label, value] of paymentFields) {
    drawField(doc, label, value, margin, y, pageW - margin * 2);
    y += 7;
  }
  y += 5;

  // ── Amount Box ──
  doc.setFillColor(240, 247, 255);
  doc.setDrawColor(30, 58, 95);
  doc.setLineWidth(1);
  doc.roundedRect(margin, y, pageW - margin * 2, 28, 3, 3, 'FD');
  doc.setFontSize(11);
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.text('AMOUNT PAID', pageW / 2, y + 9, { align: 'center' });
  doc.setFontSize(26);
  doc.setTextColor(30, 58, 95);
  doc.setFont('helvetica', 'bold');
  doc.text(`₹${data.amount.toLocaleString('en-IN')}`, pageW / 2, y + 22, { align: 'center' });

  y += 35;

  // ── Fee Summary ──
  if (data.totalFee) {
    drawSection(doc, 'FEE SUMMARY', margin, y, pageW - margin * 2);
    y += 8;
    const summaryFields = [
      ['Total Course Fee', `₹${data.totalFee.toLocaleString('en-IN')}`],
      ['Total Paid (including this)', `₹${(data.paidSoFar || 0).toLocaleString('en-IN')}`],
      ['Balance Remaining', `₹${Math.max(0, data.balance || 0).toLocaleString('en-IN')}`],
    ];
    for (const [label, value] of summaryFields) {
      drawField(doc, label, value, margin, y, pageW - margin * 2);
      y += 7;
    }
    y += 5;
  }

  // ── PAID Stamp ──
  doc.setDrawColor(22, 163, 74);
  doc.setTextColor(22, 163, 74);
  doc.setLineWidth(1.5);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  // Rotated stamp effect
  doc.text('✓ PAID', pageW - margin - 45, y, { angle: -15 });

  y += 15;

  // ── Footer ──
  addFooter(doc, pageW, margin);

  doc.save(`Receipt_${data.receiptNo}_${data.studentName.replace(/\s+/g, '_')}.pdf`);
}

// ── Helpers ──

function drawSection(doc: any, title: string, x: number, y: number, w: number) {
  doc.setFillColor(30, 58, 95);
  doc.rect(x, y - 1, w, 7, 'F');
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text(title, x + 3, y + 4);
}

function drawField(doc: any, label: string, value: string, x: number, y: number, w: number) {
  doc.setFontSize(7.5);
  doc.setTextColor(120, 120, 120);
  doc.setFont('helvetica', 'normal');
  doc.text(label + ':', x, y);
  doc.setTextColor(30, 30, 30);
  doc.setFont('helvetica', 'bold');
  const maxW = w - 35;
  const truncated = doc.splitTextToSize(value || '—', maxW)[0];
  doc.text(truncated, x + 32, y);
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.2);
  doc.line(x, y + 1.5, x + w, y + 1.5);
}

function addFooter(doc: any, pageW: number, margin: number) {
  const pageH = 297;
  doc.setFillColor(30, 58, 95);
  doc.rect(0, pageH - 18, pageW, 18, 'F');
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text(INSTITUTE_BRANDING.keonics, pageW / 2, pageH - 11, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text(`${INSTITUTE_BRANDING.fullName}  |  ${INSTITUTE_BRANDING.website}`, pageW / 2, pageH - 5, { align: 'center' });
}
